import { useAppointment, useProfile } from '@hook'
import { DateInputField, FormButton, Loader, Modal } from '@ui'
import { getError, showToast } from '@util'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import type { AppointmentTimeSlot } from './components/BookAppointmentTypes'

type RescheduleForm = {
  date: Date | null
  time: AppointmentTimeSlot | null
}

export type RescheduleAppointmentProps = {
  onClose: () => void
  isOpen?: boolean
  id?: string
}

type TimeslotItem = { startTime: string; endTime: string }

const formatSlotLabel = (slot: TimeslotItem) => {
  const start = moment(slot.startTime)
  const end = moment(slot.endTime)
  if (!start.isValid() || !end.isValid()) {
    return `${slot.startTime} – ${slot.endTime}`
  }
  return `${start.format('h:mm A')} – ${end.format('h:mm A')}`
}

const slotKey = (slot: TimeslotItem) => `${slot.startTime}\u0000${slot.endTime}`

const slotsMatch = (a: AppointmentTimeSlot | null, b: TimeslotItem) =>
  !!a && a.startTime === b.startTime && a.endTime === b.endTime

const RescheduleAppointment = ({ onClose, id }: RescheduleAppointmentProps) => {
  const { profile } = useProfile()
  const methods = useForm<RescheduleForm>({
    defaultValues: { date: null, time: null }
  })
  const { watch, setValue, reset, handleSubmit, formState, getValues } = methods
  const [staffId, setStaffId] = useState<string | undefined>(undefined)
  const [facilityId, setFacilityId] = useState<string | undefined>(undefined)
  const date = watch('date')
  const selectedTime = watch('time')
  const dateStr = date ? moment(date).format('YYYY-MM-DD') : undefined

  const { appointmentIsLoading, singleAppointment, timeslotIsLoading, timeslots, updateAppointment } = useAppointment({
    date: dateStr,
    staff_id: staffId,
    facility_id: facilityId,
    id
  })

  useEffect(() => {
    if (singleAppointment) {
      reset({
        date: moment(singleAppointment.attributes.appointment_at).toDate(),
        time: { startTime: singleAppointment.attributes.start_time, endTime: singleAppointment.attributes.end_time }
      })
      setStaffId(singleAppointment.relationships?.staff?.data?.id)
      setFacilityId(singleAppointment.relationships?.facility?.data?.id)
    }
  }, [singleAppointment, reset])

  const slotList = useMemo((): TimeslotItem[] => {
    const raw = timeslots?.timeslots
    return Array.isArray(raw) ? raw : []
  }, [timeslots])

  const onSubmit = handleSubmit(async () => {
    const { date: formDate, time: formTime } = getValues()
    if (!formDate || !formTime?.startTime || !formTime?.endTime || !profile?.id) return
    const payload = {
      data: {
        attributes: {
          appointment_at: moment(formDate).format('YYYY-MM-DD'),
          end_time: formTime.endTime,
          start_time: formTime.startTime
        },
        type: 'appointment'
      }
    }
    try {
      await updateAppointment({ data: payload, id: id || '' })
      showToast('Appointment rescheduled successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  })

  const hasDate = Boolean(date)

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Reschedule Appointment">
        <form className="flex w-full min-w-0 max-w-[500px] flex-col bg-white" onSubmit={onSubmit}>
          {appointmentIsLoading ? (
            <Loader />
          ) : (
            <div className="flex flex-col gap-5 overflow-auto px-8 py-5 min-h-[420px]">
              <DateInputField dateFormat="dd MMMM, yyyy" label="Date" name="date" placeholder="Select date" />

              <div>
                <p className="mb-2 text-sm font-medium text-[#2E2E2E]">Available time slot</p>
                {!hasDate ? (
                  <p className="text-sm text-[#6B7280]">Select a date to load available times.</p>
                ) : timeslotIsLoading ? (
                  <div aria-label="Loading time slots" className="grid grid-cols-2 md:grid-cols-4 gap-2" role="status">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div className="h-10 animate-pulse rounded-lg bg-[#F3F3F3]" key={i} />
                    ))}
                  </div>
                ) : slotList.length === 0 ? (
                  <p className="text-sm text-[#6B7280]">No time slots are available for this date. Try another day.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {slotList.map(slot => {
                      const value: AppointmentTimeSlot = { endTime: slot.endTime, startTime: slot.startTime }
                      const isActive = slotsMatch(selectedTime, slot)
                      return (
                        <button
                          className={`min-h-10 rounded-lg border px-1 py-2 text-xs leading-tight transition-colors ${
                            isActive
                              ? 'border-[#A79A46] bg-[#A79A46] text-white'
                              : 'border-[#ECECEC] bg-white text-[#2E2E2E] hover:border-[#DDD8B0]'
                          }`}
                          key={slotKey(slot)}
                          onClick={() => setValue('time', value, { shouldDirty: true, shouldValidate: true })}
                          type="button"
                        >
                          {formatSlotLabel(slot)}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <FormButton
            calcelRightIcon
            cancel="Cancel"
            disabled={!date || !selectedTime?.startTime || !selectedTime?.endTime}
            isLoading={formState.isSubmitting}
            justify="between"
            onClose={onClose}
            submit="Reschedule"
            type="submit"
          />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default RescheduleAppointment
